<template>
  <div class="visualization-layout">
    <div class="page-parts-container">
      <draggable
        v-model="localPageParts"
        class="page-parts-list"
        :animation="150"
        ghost-class="ghost"
        :disabled="isRevision"
        @start="pagePartDragStart"
        @end="pagePartDragEnd"
      >
        <div v-for="pagePart in localPageParts" :key="pagePart.id" class="page-part" :class="{ 'dragging': isDragging }">
          <div class="page-part-header">
            <div class="page-part-title" @click.stop>
              <span>{{ getPagePartLanguageName(pagePart) }}</span>
              <span class="block-type">{{ $t(getBlockTypeKey(pagePart.blockType)) }}</span>
              <el-button
                type="text"
                icon="el-icon-edit"
                :disabled="isRevision"
                @click="handlePagePartConfig(pagePart)"
              />
            </div>
            <el-button
              type="text"
              icon="el-icon-close"
              :disabled="isRevision"
              @click="removePagePart(pagePart)"
            />
          </div>
          <div class="elements-container">
            <draggable
              v-model="pagePart.elements"
              class="elements-grid"
              :animation="150"
              ghost-class="element-ghost"
              group="elements"
              :disabled="isRevision"
              @start="elementDragStart($event, pagePart)"
              @end="elementDragEnd($event, pagePart)"
            >
              <div v-for="element in pagePart.elements" :key="element.uuid" class="element-item">
                <el-button
                  :title="getElementFullPath(element)"
                  :class="{ 'required': element.isRequire || element.isDefaultRequire }"
                  :disabled="isRevision"
                  @click="handleElementClick(element)"
                >{{ getElementName(element) }}</el-button>
                <el-button
                  type="text"
                  icon="el-icon-close"
                  class="remove-element"
                  :disabled="isRevision"
                  @click="removeElement(pagePart, element)"
                />
              </div>
            </draggable>
          </div>
        </div>
      </draggable>
    </div>

    <!-- 字段配置对话框 -->
    <ai-custom-page-element-config
      v-if="elementConfigVisible"
      :visible.sync="elementConfigVisible"
      :element="currentElement"
      @confirm="handleElementConfigConfirm"
    />

    <!-- 页面分块配置对话框 -->
    <ai-custom-page-page-part-config
      v-if="pagePartConfigVisible"
      :visible.sync="pagePartConfigVisible"
      :page-part="currentPagePart"
      :is-create-page-part="isCreatePagePart"
      :application-scene="applicationScene"
      :business-type="businessType"
      @confirm="handlePagePartConfigConfirm"
      @remove-page-part="removePagePart"
    />
  </div>
</template>

<script>
import draggable from 'vuedraggable'
import { getLocalizedName, getCurrentLangCode } from '@/utils/langUtil'
import * as UICustomConstants from '@/class/cargo/model/parameters/UICustomConstants'
import { MAX_PAGE_PARTS, DEFAULT_PAGE_PART, PAGE_PART_TYPE } from '@/class/cargo/model/parameters/AiCustomPageConstants'
import AiCustomPageElementConfig from './aiCustomPageElementConfig.vue'
import AiCustomPagePagePartConfig from './aiCustomPagePagePartConfig.vue'

export default {
  name: 'AiCustomPageVisualizationView',
  components: {
    draggable,
    AiCustomPageElementConfig,
    AiCustomPagePagePartConfig
  },
  props: {
    pageParts: {
      type: Array,
      required: true
    },
    isRevision: {
      type: Boolean,
      default: false
    },
    applicationScene: {
      type: Number,
      default: null
    },
    businessType: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      isDragging: false,
      isElementDragging: false,
      elementConfigVisible: false,
      currentElement: null,
      pagePartConfigVisible: false,
      currentPagePart: null,
      isCreatePagePart: false,
      PAGE_PART_TYPE,
      // 左侧树形结构映射
      leftTreeMap: {
        [UICustomConstants.SEA_VERIFY_BOOKING_BASIC]: {
          path: ['SE', 'bookingVerify', 'basic'],
          names: [
            this.$t('CargoMsg.Cargo_OceanExport'),
            this.$t('CargoMsg.Cargo_BookingPreAudit'),
            this.$t('SystemMaintenance.BasicTab')
          ]
        },
        [UICustomConstants.SEA_VERIFY_BOOKING_VESSEL]: {
          path: ['SE', 'bookingVerify', 'vesselEq'],
          names: [
            this.$t('CargoMsg.Cargo_OceanExport'),
            this.$t('CargoMsg.Cargo_BookingPreAudit'),
            this.$t('Message.BookingLabel')
          ]
        },
        [UICustomConstants.SEA_BOOKING_BASIC]: {
          path: ['SE', 'bookingNew', 'basic'],
          names: [
            this.$t('CargoMsg.Cargo_OceanExport'),
            this.$t('CargoMsg.Cargo_SeaBooking'),
            this.$t('SystemMaintenance.BasicTab')
          ]
        },
        [UICustomConstants.SEA_BOOKING_VESSEL]: {
          path: ['SE', 'bookingNew', 'vesselEq'],
          names: [
            this.$t('CargoMsg.Cargo_OceanExport'),
            this.$t('CargoMsg.Cargo_SeaBooking'),
            this.$t('Message.BookingLabel')
          ]
        },
        [UICustomConstants.SEA_BOOKING_MBL]: {
          path: ['SE', 'bookingNew', 'mbl'],
          names: [
            this.$t('CargoMsg.Cargo_OceanExport'),
            this.$t('CargoMsg.Cargo_SeaBooking'),
            'MBL' + this.$t('SeaExport.tab')
          ]
        },
        [UICustomConstants.SEA_BOOKING_HBL]: {
          path: ['SE', 'bookingNew', 'hbl'],
          names: [
            this.$t('CargoMsg.Cargo_OceanExport'),
            this.$t('CargoMsg.Cargo_SeaBooking'),
            'HBL' + this.$t('SeaExport.tab')
          ]
        },
        [UICustomConstants.SEA_BOOKING_VIRTUAL_HBL]: {
          path: ['SE', 'bookingNew', 'virtualHbl'],
          names: [
            this.$t('CargoMsg.Cargo_OceanExport'),
            this.$t('CargoMsg.Cargo_SeaBooking'),
            this.$t('SeaExport.FormalHbl')
          ]
        }
      }
    }
  },
  computed: {
    localPageParts: {
      get() {
        return this.pageParts
      }
    }
  },
  emits: ['update:pageParts', 'remove-page-part', 'remove-element', 'page-part-type-changed'],
  created() {
    // 初始化当前页面部件为第一个页面部件
    if (this.pageParts && this.pageParts.length > 0) {
      this.currentPagePart = { ...this.pageParts[0] }
    }
  },
  methods: {
    // 获取本地化名称
    getLocalizedName(data) {
      return getLocalizedName(data)
    },
    // 获取元素名称
    getElementName(element) {
      if (!element) return this.$t('CargoMsg.Cargo_Unknown')
      return this.getLocalizedName(element)
    },
    // 获取分块类型key
    getBlockTypeKey(blockType) {
      const type = Object.values(this.PAGE_PART_TYPE).find(type => type.value === blockType)
      return type?.key || this.PAGE_PART_TYPE.UNKNOWN.key
    },
    handleAddPagePart() {
      if (this.pageParts.length >= MAX_PAGE_PARTS) {
        this.$message.warning(this.$t('CargoMsg.Cargo_MaxPagePartsReached', { 0: MAX_PAGE_PARTS }))
        return
      }
      const counter = this.pageParts.length + 1
      const newPagePart = DEFAULT_PAGE_PART(counter)
      this.pageParts.push(newPagePart)
      this.$emit('update:pageParts', this.pageParts)
    },
    handlePagePartConfigConfirm(updatedPagePart) {
      const index = this.pageParts.findIndex(p => p.id === updatedPagePart.id)
      if (index !== -1) {
        this.pageParts[index] = updatedPagePart
        this.$emit('update:pageParts', this.pageParts, updatedPagePart)
      }
    },
    removePagePart(pagePart, isCreatePagePart = false) {
      this.$emit('remove-page-part', pagePart, isCreatePagePart)
      this.$emit('update:pageParts', this.pageParts)
    },
    removeElement(pagePart, element) {
      this.$emit('remove-element', pagePart, element)
      this.$emit('update:pageParts', this.pageParts)
    },
    pagePartDragStart(evt) {
      this.isDragging = true
      console.log('[调试] 页面部件拖拽开始 - 事件详情:', {
        evt,
        pageParts: this.pageParts
      })
    },
    pagePartDragEnd(evt) {
      this.isDragging = false
      console.log('[调试] 页面部件拖拽结束 - 事件详情:', {
        evt,
        movedPagePart: evt.item._underlying_vm_,
        oldIndex: evt.oldIndex,
        newIndex: evt.newIndex
      })
      if (evt.oldIndex === evt.newIndex) {
        return
      }

      // 使用事件中的移动信息更新顺序
      const newPageParts = [...this.pageParts]
      const [movedPagePart] = newPageParts.splice(evt.oldIndex, 1)
      // 如果是从后往前移动，需要调整newIndex
      const adjustedNewIndex = evt.oldIndex < evt.newIndex ? evt.newIndex - 1 : evt.newIndex
      newPageParts.splice(adjustedNewIndex, 0, movedPagePart)
      console.log('[调试] 更新后的页面部件顺序:', newPageParts)
      this.$emit('update:pageParts', newPageParts)
    },
    elementDragStart(evt, sourcePagePart) {
      console.log('[调试] 拖拽开始，事件和来源区域:', { evt, sourcePagePart })

      this.isElementDragging = true

      // 为所有空页面区域添加临时元素，除了源区域
      this.pageParts.forEach(pagePart => {
        if (pagePart.blockType === PAGE_PART_TYPE.BUSINESS_ELEMENTS.value &&
          (pagePart.elements.length === 0 && pagePart.id !== sourcePagePart.id)) {
          // 添加临时元素来处理空区域无法拖拽的问题，限于业务字段类型的分块
          pagePart.elements.push({ elementName: null })
        }
      })
    },
    elementDragEnd(evt, sourcePagePart) {
      console.log('[调试] 拖拽结束，事件和来源区域:', { evt, sourcePagePart })

      this.isElementDragging = false
      console.log('[调试] 元素拖拽结束 - 更新前:', this.pageParts)

      // 检查目标页面区域是否为业务字段分块
      if (sourcePagePart.blockType !== PAGE_PART_TYPE.BUSINESS_ELEMENTS.value) {
        console.log('[调试] 非业务字段分块，不允许拖拽元素')

        return
      }

      console.log('[调试] 跨区域移动 - 来源区域:', sourcePagePart)

      // 根据放置位置计算新位置
      const dropIndex = evt.newIndex
      const elements = sourcePagePart.elements

      // 确保每行保持4个元素
      const currentCol = dropIndex % 4

      // 如果在一行末尾，移动到下一行
      if (currentCol === 3 && dropIndex < elements.length - 1) {
        const nextElement = elements[dropIndex + 1]
        elements.splice(dropIndex + 1, 1)
        elements.splice(dropIndex, 0, nextElement)
      }

      // 移除所有临时元素
      this.pageParts.forEach((pagePart, index) => {
        const filteredElements = pagePart.elements.filter(e => e.elementName !== null)
        this.$set(this.pageParts, index, { ...pagePart, elements: filteredElements })
      })

      console.log('[调试] 元素拖拽结束 - 更新后:', this.pageParts)
      // 触发更新事件
      this.$emit('update:pageParts', this.pageParts)
    },
    // 获取元素的完整路径
    getElementFullPath(element) {
      if (!element || !element.pageType) return ''
      const treeInfo = this.leftTreeMap[element.pageType]
      if (!treeInfo) return ''
      // 获取左侧树的三级路径
      const leftTreePath = treeInfo.names.join(' > ')
      // 获取元素名称
      const elementName = this.getElementName(element)
      // TODO: 获取分块名称目前有点问题，之后解决

      // 组合完整路径
      return `${leftTreePath} > ${elementName}`
    },
    handleElementClick(element) {
      this.currentElement = element
      this.elementConfigVisible = true
    },
    handleElementConfigConfirm(updatedElement) {
      console.log('[调试] 收到元素配置更新:', updatedElement)
      // 更新元素配置
      const pagePart = this.pageParts.find(p => p.elements.some(e => e.elementName === updatedElement.elementName))
      if (pagePart) {
        const elementIndex = pagePart.elements.findIndex(e => e.elementName === updatedElement.elementName)
        if (elementIndex > -1) {
          // 只更新isReadonly和isRequire字段，确保响应式
          this.$set(pagePart.elements[elementIndex], 'isReadonly', updatedElement.isReadonly)
          this.$set(pagePart.elements[elementIndex], 'isRequire', updatedElement.isRequire)
          console.log('[调试] 更新后 pagePart:', pagePart)
          console.log('[调试] 更新后 pageParts:', this.pageParts)
          this.$emit('update:pageParts', this.pageParts)
        }
      }
    },
    getPagePartLanguageName(pagePart) {
      // 获取当前语言代码并在languageList中查找对应的语言值
      const currentLangCode = getCurrentLangCode()
      const language = pagePart.languageList.find(l => l.language === currentLangCode)
      return language ? language.name : pagePart.name
    },
    handlePagePartConfig(pagePart, isCreatePagePart = false) {
      console.log('[调试] 点击分块配置按钮:', pagePart)
      this.currentPagePart = { ...pagePart }
      this.pagePartConfigVisible = true
      this.isCreatePagePart = isCreatePagePart
      console.log('[调试] 分块配置状态值:', {
        currentPagePart: this.currentPagePart,
        pagePartConfigVisible: this.pagePartConfigVisible,
        isCreatePagePart: this.isCreatePagePart
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.visualization-layout {
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  .page-parts-container {
    flex: 1;
    padding: 1rem;
    overflow-y: auto;
    min-height: 0;
    display: flex;
    flex-direction: column;

    .page-parts-list {
      flex: 1;
      min-height: 0;
    }

    .page-part {
      background: #f8f9fa;
      border-radius: 4px;
      padding: 16px;
      margin-bottom: 16px;
      transition: all 0.3s;
      cursor: move;

      &.dragging {
        opacity: 0.5;
        background: #e6f7ff;
        border: 2px dashed #1890ff;
      }

      .page-part-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 16px;

        .page-part-title {
          display: flex;
          align-items: center;
          gap: 4px;

          .block-type {
            color: #909399;
            font-size: 0.9em;
            margin-left: 4px;
          }
        }
      }

      .elements-container {
        margin-bottom: 16px;
        min-height: 50px;

        .elements-grid {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 8px;

          .element-item {
            display: flex;
            align-items: center;
            gap: 4px;
            cursor: move;
            transition: all 0.3s;
            min-width: 0;
            transform: translateY(-2px);
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
            position: relative;

            .el-button {
              min-width: 0;
              flex: 1;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
              text-align: center;
              background: #fff;
              border: 1px solid #dcdfe6;
              border-radius: 2px;
              color: #303133;

              &.required {
                color: #409EFF;
              }
            }

            .remove-element {
              position: absolute;
              top: 2px;
              right: 2px;
              padding: 0;
              margin: 0;
              flex-shrink: 0;
              z-index: 1;
              font-weight: bold;
              opacity: 0;
              transition: opacity 0.3s;
            }

            &:hover {
              .remove-element {
                opacity: 1;
              }
            }
          }
        }
      }
    }
  }
}

.ghost {
  opacity: 0.5;
  background: #e6f7ff;
  border: 2px dashed #1890ff;
}

.element-ghost {
  opacity: 0.5;
  background: #e6f7ff;
  border: 2px dashed #1890ff;
}
</style>
